import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchFinanceSummary, syncFinance } from '../../api.js'
import FinanceNav from './FinanceNav.jsx'

/**
 * Finance.jsx (FEDIS) — Hub Compta/Finance
 * ------------------------------------------------------------
 * Objectif :
 *  - Page "porte d'entrée" de la compta structurée
 *  - Affiche une synthèse de pilotage (utile) + liens vers :
 *      • Journal (écritures double entrée)
 *      • Exports
 *  - Robuste : si l’API n’est pas branchée => hint propre, pas de crash.
 *
 * API attendue :
 *  - GET /api/finance/summary?from=YYYY-MM-DD&to=YYYY-MM-DD
 *    => { ventes, achats, depenses, resultat, journal_stats? }
 */

function toNum(v, def = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : def
}

function isoDate(d) {
  const dt = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toISOString().slice(0, 10)
}

function fmtDateFR(d) {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleDateString('fr-FR')
}

function fmtMoneyXOF(v) {
  return `${toNum(v, 0).toLocaleString('fr-FR')} XOF`
}

function startOfMonth(now = new Date()) {
  return new Date(now.getFullYear(), now.getMonth(), 1)
}
function endOfMonth(now = new Date()) {
  return new Date(now.getFullYear(), now.getMonth() + 1, 0)
}

const BORDER_LEFT = {
  indigo: 'border-slate-400',
  green: 'border-green-500',
  red: 'border-red-500',
  blue: 'border-slate-400',
  amber: 'border-amber-500',
  slate: 'border-slate-500',
}

function KpiCard({ title, value, subtitle, color = 'indigo', loading = false }) {
  const borderClass = BORDER_LEFT[color] || BORDER_LEFT.indigo
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${borderClass} rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow`}>
      <div className="text-slate-500 text-sm font-medium">{title}</div>
      {loading ? (
        <div className="h-8 bg-gray-200 rounded animate-pulse mt-2" />
      ) : (
        <>
          <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
          {subtitle ? <div className="text-xs text-slate-500 mt-2">{subtitle}</div> : null}
        </>
      )}
    </div>
  )
}

export default function Finance() {
  // période (défaut: mois en cours)
  const defaultRange = useMemo(() => {
    const now = new Date()
    return { from: isoDate(startOfMonth(now)), to: isoDate(endOfMonth(now)) }
  }, [])

  const [from, setFrom] = useState(defaultRange.from)
  const [to, setTo] = useState(defaultRange.to)

  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [hint, setHint] = useState(null)
  const [summary, setSummary] = useState(null)
  const [syncing, setSyncing] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    setHint(null)

    try {
      const data = await fetchFinanceSummary({ from, to })
      setSummary(data)
    } catch (e) {
      setSummary(null)
      const msg = String(e?.message || e || '')
      const is404 =
        e?.status === 404 ||
        /\b404\b/.test(msg) ||
        /Cannot GET/i.test(msg) ||
        /not found/i.test(msg)

      if (is404) {
        setHint("Endpoint manquant: /api/finance/summary (à implémenter côté backend).")
      } else {
        setErr(msg || 'Erreur chargement finance.')
      }
    } finally {
      setLoading(false)
    }
  }, [from, to])

  useEffect(() => { load() }, [load])

  const ca = toNum(summary?.ventes?.ca_total)
  const achats = toNum(summary?.achats?.total_achats)
  const dep = toNum(summary?.depenses?.total_depenses)
  const net = toNum(summary?.resultat?.benefice_net)
  const encaissements = toNum(summary?.paiements?.total)
  const creances = toNum(summary?._raw?.totals?.outstanding)
  const nbEntries = toNum(summary?.journal_stats?.entries_count ?? summary?.journal_stats?.count)
  const nbLines = toNum(summary?.journal_stats?.lines_count)

  return (
    <div className="p-6 space-y-6 min-h-screen">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Comptabilité • Finance</h1>
          <p className="text-slate-500 text-sm mt-1">
            Période : <span className="font-semibold">{fmtDateFR(from)}</span> → <span className="font-semibold">{fmtDateFR(to)}</span>
          </p>
        </div>

        <div className="flex gap-2">
          <Link
            to="/finance/journal"
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950 transition-colors font-semibold text-sm"
          >
            📚 Journal
          </Link>
          <Link
            to="/finance/export"
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950 transition-colors font-semibold text-sm"
          >
            📦 Exports
          </Link>
          <button
            type="button"
            onClick={async () => {
              setSyncing(true)
              try {
                await syncFinance({ from, to })
                await load()
              } finally {
                setSyncing(false)
              }
            }}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold text-sm"
          >
            {syncing ? 'Sync…' : '↻ Sync Finance'}
          </button>
        </div>
      </div>

      <FinanceNav />

      {/* Range */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500 block mb-1 font-medium">Du</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="border border-slate-200 px-3 py-2 rounded-lg w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
            />
          </div>

          <div>
            <label className="text-xs text-slate-500 block mb-1 font-medium">Au</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="border border-slate-200 px-3 py-2 rounded-lg w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
            />
          </div>

          <button
            type="button"
            onClick={load}
            disabled={loading || !from || !to}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950 disabled:opacity-50 disabled:cursor-not-allowed h-[42px] font-semibold"
          >
            {loading ? 'Chargement…' : '🔄 Actualiser'}
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          <strong>Erreur :</strong> {err}
        </div>
      )}

      {!err && hint && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-900 text-sm">
          <div className="font-semibold">🧩 API Finance non branchée</div>
          <div className="mt-1">{hint}</div>
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard title="Chiffre d'affaires" value={fmtMoneyXOF(ca)} subtitle="Ventes sur période" color="green" loading={loading} />
        <KpiCard title="Achats" value={fmtMoneyXOF(achats)} subtitle="Approvisionnement" color="blue" loading={loading} />
        <KpiCard title="Dépenses" value={fmtMoneyXOF(dep)} subtitle="Charges opérationnelles" color="amber" loading={loading} />
        <KpiCard title="Résultat net" value={fmtMoneyXOF(net)} subtitle="CA - Achats - Dépenses" color={net >= 0 ? 'green' : 'red'} loading={loading} />
        <KpiCard title="Encaissements" value={fmtMoneyXOF(encaissements)} subtitle="Paiements reçus" color="indigo" loading={loading} />
        <KpiCard title="Créances" value={fmtMoneyXOF(creances)} subtitle="Clients débiteurs" color="slate" loading={loading} />
      </div>

      {/* Bloc "Compta structurée" */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-bold text-lg text-slate-900">🧾 Comptabilité structurée</h2>
            <div className="text-sm text-slate-500 mt-1">
              Ici, l’objectif n’est pas de refaire Dashboard/Reports, mais de sortir des écritures “Débit/Crédit”
              (journal, grand livre, balance).
            </div>
          </div>
          <div className="text-xs text-slate-500">
            {Number.isFinite(nbEntries) && nbEntries > 0 ? (
              <>Écritures: <strong>{nbEntries.toLocaleString('fr-FR')}</strong> • Lignes: <strong>{nbLines.toLocaleString('fr-FR')}</strong></>
            ) : (
              <>Stats journal: —</>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/finance/journal" className="px-3 py-2 rounded-lg bg-slate-50 text-slate-900 hover:bg-slate-100 text-sm font-semibold">
            Ouvrir Journal →
          </Link>
          <Link to="/finance/export" className="px-3 py-2 rounded-lg bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 text-sm font-semibold">
            Exports →
          </Link>
        </div>
      </div>
    </div>
  )
}
